/**
 * Emma AI on the marketing site (scripts/build-site.mjs loads /bundle/emma-site.js as a module on every page):
 * the shared widget with a compact label table for sq/en and plain fetch — the visitor routes /v1/public/emma/*.
 * The door forms (`[data-faq-form]`) dispatch `emma:ask` {detail:{text}} (site.js); the widget listens, opens
 * and sends the text as a turn. Nothing from the app dictionaries is imported here.
 */
import { mountEmma, type EmmaLocale } from './ui/emma.js';

const LABELS: Record<EmmaLocale, Record<string, string>> = {
  sq: {
    'emma.title': 'Emma',
    'emma.subtitle': 'Asistentja AI e Vepra-s',
    'emma.ask': 'Pyet Emma-n',
    'emma.placeholder': 'Shkruaj pyetjen tënde…',
    'emma.send': 'Dërgo',
    'emma.chips.label': 'Zgjedhjet e Emma-s',
    'emma.chips.more': '+{n} të tjera',
    'emma.minimize': 'Minimizo',
    'emma.new': 'Bisedë e re',
    'emma.more': 'Më shumë',
    'emma.askOther': 'Pyet diçka tjetër…',
    'emma.doors.more': 'Më shumë (+{n})',
    'emma.thinking': 'Po kontrolloj…',
    'emma.working.prepare': 'Po përgatis…',
    'emma.working.offer': 'Po marr ofertën…',
    'emma.working.availability': 'Po kërkoj disponueshmërinë…',
    'emma.stale': 'Hapi ka ndryshuar; zgjidh sërish.',
    'emma.usage': '{used}/{limit} përgjigje të gjeneruara këtë muaj · rinovohet më {renews}',
    'emma.usage.day': '{used}/{limit} përgjigje të gjeneruara sot · rinovohet më {renews}',
    'emma.status.off': 'Nga rregullat dhe njohuritë e miratuara · njohuri {version}',
    'emma.status.offline': 'Modeli i lirë nuk është i lidhur',
    'emma.status.ready': 'Modeli lokal gati',
    'emma.status.error': 'Modeli me gabim; përgjigjem nga burimet lokale',
    'emma.checked': 'kontrolluar {date}',
    'emma.flag.ask': 'Përgjigje e gabuar?',
    'emma.flag.sent': 'Faleminderit — ekipi e shqyrton. Përgjigjja ndryshon vetëm pasi ta miratojnë dy persona.',
    'emma.flag.failed': 'Raportimi nuk u dërgua. Provo sërish.',
    'emma.link.newTab': 'hapet në skedë të re',
    'emma.failed': 'Emma nuk u përgjigj; provo sërish.',
    'emma.retry': 'Provo sërish',
    'emma.greetingFallback': 'Si mund të të ndihmoj?',
    'emma.change.placeholder': 'Vlera e re…',
    'emma.change.send': 'Ndrysho',
    'emma.cancel': 'Anulo',
    // Vala C: offline, the team, the case link, the permission card (the same keys as sq.ts / en.ts)
    'emma.offline': 'Je pa lidhje. Mesazhi yt mbetet këtu; dërgoje kur të kthehet lidhja.',
    'emma.handoff': 'Biseda është te ekipi; Emma nuk përgjigjet derisa ekipi ta kthejë.',
    'emma.case': 'Rasti {id} · hap',
    'emma.perm.FORBIDDEN': 'Roli yt nuk e ka lejen për këtë veprim.',
    'emma.perm.FORBIDDEN.next': 'Kërkoja pronarit ose administratorit të biznesit.',
    'emma.perm.LICENSE_REQUIRED': 'Licenca e biznesit nuk e lejon këtë veprim tani.',
    'emma.perm.LICENSE_REQUIRED.next': 'Te Licenca shihet gjendja dhe hapi i vetëm që mbetet.',
    'emma.perm.LICENSE_REQUIRED.link': 'Hap Licencën',
    'emma.perm.ENTITLEMENT_REQUIRED': 'Ky modul nuk përfshihet në planin aktual.',
    'emma.perm.ENTITLEMENT_REQUIRED.next': 'Plani ndryshohet te Licenca; moduli nuk ndizet pa të.',
    'emma.perm.ENTITLEMENT_REQUIRED.link': 'Shiko planin',
    'emma.perm.MODULE_DISABLED': 'Moduli është i fikur për këtë biznes.',
    'emma.perm.MODULE_DISABLED.next': 'E ndez pronari te Modulet; nëse nuk je pronar, kërkoja atij.',
    'emma.perm.MODULE_DISABLED.link': 'Hap Modulet',
    'emma.perm.MFA_REQUIRED': 'Ky veprim kërkon verifikimin me dy hapa.',
    'emma.perm.MFA_REQUIRED.next': 'Aktivizoje ose konfirmoje te siguria e llogarisë, pastaj provo sërish.',
    'emma.perm.MFA_REQUIRED.link': 'Hap sigurinë e llogarisë',
    'emma.perm.writeToolsOff': 'Veprimet e Emma-s me shkrim janë të fikura për këtë biznes.',
    'emma.perm.writeToolsOff.next': 'Leximet vazhdojnë. Shkrimet i ndez platforma për këtë biznes.',
    'emma.perm.skillOff': 'Kjo aftësi e Emma-s është e fikur për këtë biznes.',
    'emma.perm.skillOff.next': 'E ndez sërish pronari ose administratori i biznesit.',
    'emma.perm.platformArea': 'Kjo zonë e platformës nuk është e hapur për rolin tënd.',
    'emma.perm.platformArea.next': 'Zonat e rolit i cakton superadmini te Përdoruesit e platformës.',
    // Vala D: the page the server's diagnosis names (the same key as sq.ts / en.ts)
    'emma.perm.open': 'Hap faqen',
    // Agjenti i porosive 2: the order card and the location chip (the same keys as sq.ts / en.ts)
    'emma.order.geoWait': 'Po marrim pozicionin…',
    'emma.order.title.trip': 'Udhëtimi yt',
    'emma.order.title.menu': 'Porosia jote',
    'emma.order.title.booking': 'Rezervimi yt',
    'emma.order.title.stay': 'Qëndrimi yt',
    'emma.order.title.case': 'Kërkesa jote',
    'emma.order.title.service': 'Kërkesa jote',
    'emma.order.open': 'Hap faqen',
    'emma.order.stopped': 'Ndjekja këtu u ndal; statusi është gjithmonë te faqja.',
    'emma.order.searching': 'Po kërkohet shoferi më i afërt… (dispeçeri automatik)',
    'emma.order.until': 'afati {time}',
    'emma.order.searchingManual': 'Kërkojmë shofer deri në {time}',
    'emma.order.waiting': 'Po pritet caktimi i shoferit…',
    'emma.order.asked1': 'U pyet 1 shofer',
    'emma.order.asked': 'U pyetën {n} shoferë',
    'emma.order.live': 'njëri po e shikon ofertën tani',
    'emma.order.noDriver': 'Nuk u gjet shofer i lirë — asgjë nuk u ngarkua. Kërko një ofertë të re.',
    'emma.order.accepted': 'Shoferi pranoi.',
    'emma.order.arriving': 'Shoferi u nis te ti.',
    'emma.order.arrived': 'Shoferi mbërriti — dil te pika e takimit.',
    'emma.order.inRide': 'Udhëtimi nisi.',
    'emma.order.completed': 'Udhëtimi përfundoi.',
    'emma.order.cancelled': 'Udhëtimi u anulua ose u mbyll.',
    'emma.order.eta': 'Mbërrin për rreth {min} min',
    'emma.order.estimate': 'vlerësim',
    'emma.order.menuNew': 'Pret pranimin e biznesit',
    'emma.order.menuAccepted': 'U pranua — porosia nr. {n}',
    'emma.order.menuRejected': 'Porosia nuk u pranua',
    'emma.order.menuPay': 'Pagesa: në marrje ose në dorëzim, te biznesi.',
    'emma.order.cardBlocked': 'Pagesa online me kartë: E bllokuar',
    // Vala B: the ride card and the slide (the same keys as sq.ts / en.ts)
    'emma.ride.title': 'Oferta e udhëtimit',
    'emma.ride.from': 'Nga',
    'emma.ride.to': 'Për',
    'emma.ride.estimate': 'vlerësim',
    'emma.ride.total': 'Totali',
    'emma.ride.fiscalTaximeter': 'Kuponi fiskal lëshohet nga taksimetri',
    'emma.ride.fiscalBusiness': 'Kuponi fiskal lëshohet nga biznesi',
    'emma.ride.validUntil': 'E vlefshme deri {time}',
    'emma.ride.lead': 'Ja oferta për udhëtimin tënd:',
    'emma.ride.words': 'Mesazhi i plotë i Emma-s',
    'emma.slide.hint': 'Rrëshqit djathtas për ta konfirmuar, ose prek dhe zgjidh «Konfirmo».',
    'emma.slide.confirm': 'Konfirmo',
    'emma.card.booking': 'Rezervimi',
    'emma.card.availability': 'Orare të lira',
    'emma.card.business': 'Biznese',
    'emma.card.order': 'Porosia',
    'emma.card.approval': 'Pret miratimin',
    'emma.card.approval.note': 'Një person me leje e miraton nga faqja e rastit.',
    'emma.card.progress': 'Ecuria',
    'emma.card.finance_summary': 'Financat',
    'emma.card.financePeriod': 'Periudha {period}',
    'emma.card.fin.net_sales': 'Shitje neto',
    'emma.card.fin.expenses': 'Shpenzime',
    'emma.card.fin.net_profit': 'Fitim neto',
    'emma.card.fin.product_margin': 'Marzhi i produkteve',
    'emma.card.unknown': 'e panjohur',
    'emma.card.task': 'Detyrat e hapura',
    'emma.card.due': 'afati {date}',
    'emma.card.service': 'Shërbimi',
    'emma.card.when': 'Koha',
    'emma.card.price': 'Çmimi',
    'emma.card.status': 'Gjendja',
    'emma.card.pickSlot': 'Zgjidh {time} te {business}',
    'emma.card.state.needs_information': 'Duhen të dhëna',
    'emma.card.state.awaiting_confirmation': 'Pret konfirmimin tënd',
    'emma.card.state.executing': 'Po kryhet…',
    'emma.card.state.completed': 'U krye',
    'emma.card.state.failed': 'Nuk u krye',
    'emma.card.state.cancelled': 'U anulua',
    'emma.card.state.handed_to_human': 'Te ekipi',
    'emma.card.state.unknown_result': 'Rezultati nuk dihet',
    'emma.card.feedback': 'A ishte e dobishme kjo përgjigje?',
    'emma.card.feedback.up': 'E dobishme',
    'emma.card.feedback.down': 'Jo e dobishme',
    'emma.card.feedback.thanks': 'Faleminderit për vlerësimin.',
    'emma.card.feedback.failed': 'Vlerësimi nuk u dërgua; provo sërish.',
  },
  en: {
    'emma.title': 'Emma',
    'emma.subtitle': 'Vepra’s AI assistant',
    'emma.ask': 'Ask Emma',
    'emma.placeholder': 'Type your question…',
    'emma.send': 'Send',
    'emma.chips.label': 'Emma’s choices',
    'emma.chips.more': '+{n} more',
    'emma.minimize': 'Minimise',
    'emma.new': 'New conversation',
    'emma.more': 'More',
    'emma.askOther': 'Ask something else…',
    'emma.doors.more': 'More (+{n})',
    'emma.thinking': 'Checking…',
    'emma.working.prepare': 'Preparing…',
    'emma.working.offer': 'Getting the offer…',
    'emma.working.availability': 'Checking availability…',
    'emma.stale': 'The step changed; choose again.',
    'emma.usage': '{used}/{limit} generated answers this month · renews on {renews}',
    'emma.usage.day': '{used}/{limit} generated answers today · renews on {renews}',
    'emma.status.off': 'From the rules and the approved knowledge · knowledge {version}',
    'emma.status.offline': 'The free model is not connected',
    'emma.status.ready': 'Local model ready',
    'emma.status.error': 'Model error; answering from local sources',
    'emma.checked': 'checked {date}',
    'emma.flag.ask': 'Wrong answer?',
    'emma.flag.sent': 'Thank you — the team will review it. The answer changes only after two people approve it.',
    'emma.flag.failed': 'The report was not sent. Try again.',
    'emma.link.newTab': 'opens in a new tab',
    'emma.failed': 'Emma did not answer; try again.',
    'emma.retry': 'Retry',
    'emma.greetingFallback': 'How can I help?',
    'emma.change.placeholder': 'New value…',
    'emma.change.send': 'Change',
    'emma.cancel': 'Cancel',
    // Vala C: offline, the team, the case link, the permission card (the same keys as sq.ts / en.ts)
    'emma.offline': 'You are offline. Your message stays here; send it when the connection is back.',
    'emma.handoff': 'The conversation is with the team; Emma does not answer until the team hands it back.',
    'emma.case': 'Case {id} · open',
    'emma.perm.FORBIDDEN': 'Your role does not have the permission for this action.',
    'emma.perm.FORBIDDEN.next': 'Ask the owner or an administrator of the business.',
    'emma.perm.LICENSE_REQUIRED': 'The business licence does not allow this action right now.',
    'emma.perm.LICENSE_REQUIRED.next': 'The Licence page shows its state and the one step left.',
    'emma.perm.LICENSE_REQUIRED.link': 'Open the licence',
    'emma.perm.ENTITLEMENT_REQUIRED': 'This module is not part of the current plan.',
    'emma.perm.ENTITLEMENT_REQUIRED.next': 'The plan is changed on the Licence page; the module does not switch on without it.',
    'emma.perm.ENTITLEMENT_REQUIRED.link': 'See the plan',
    'emma.perm.MODULE_DISABLED': 'The module is switched off for this business.',
    'emma.perm.MODULE_DISABLED.next': 'The owner switches it on under Modules; if you are not the owner, ask them.',
    'emma.perm.MODULE_DISABLED.link': 'Open Modules',
    'emma.perm.MFA_REQUIRED': 'This action needs two-step verification.',
    'emma.perm.MFA_REQUIRED.next': 'Turn it on or confirm it under account security, then try again.',
    'emma.perm.MFA_REQUIRED.link': 'Open account security',
    'emma.perm.writeToolsOff': 'Emma’s write actions are off for this business.',
    'emma.perm.writeToolsOff.next': 'Reads continue. The platform switches the writes on for this business.',
    'emma.perm.skillOff': 'This Emma skill is off for this business.',
    'emma.perm.skillOff.next': 'The owner or an administrator of the business switches it back on.',
    'emma.perm.platformArea': 'This platform area is not open for your role.',
    'emma.perm.platformArea.next': 'The superadmin sets the areas of a role under Platform users.',
    // Vala D: the page the server's diagnosis names (the same key as sq.ts / en.ts)
    'emma.perm.open': 'Open the page',
    // Agjenti i porosive 2: the order card and the location chip (the same keys as sq.ts / en.ts)
    'emma.order.geoWait': 'Getting your position…',
    'emma.order.title.trip': 'Your trip',
    'emma.order.title.menu': 'Your order',
    'emma.order.title.booking': 'Your booking',
    'emma.order.title.stay': 'Your stay',
    'emma.order.title.case': 'Your request',
    'emma.order.title.service': 'Your request',
    'emma.order.open': 'Open the page',
    'emma.order.stopped': 'Following here stopped; the status is always on the page.',
    'emma.order.searching': 'Looking for the nearest driver… (automatic dispatcher)',
    'emma.order.until': 'deadline {time}',
    'emma.order.searchingManual': 'Looking for a driver until {time}',
    'emma.order.waiting': 'Waiting for a driver to be assigned…',
    'emma.order.asked1': '1 driver asked',
    'emma.order.asked': '{n} drivers asked',
    'emma.order.live': 'one is looking at the offer now',
    'emma.order.noDriver': 'No free driver was found — nothing was charged. Ask for a new quote.',
    'emma.order.accepted': 'The driver accepted.',
    'emma.order.arriving': 'The driver set off to you.',
    'emma.order.arrived': 'The driver arrived — go to the meeting point.',
    'emma.order.inRide': 'The ride started.',
    'emma.order.completed': 'The ride is completed.',
    'emma.order.cancelled': 'The trip was cancelled or closed.',
    'emma.order.eta': 'Arrives in about {min} min',
    'emma.order.estimate': 'estimate',
    'emma.order.menuNew': 'Waiting for the business to accept',
    'emma.order.menuAccepted': 'Accepted — order no. {n}',
    'emma.order.menuRejected': 'The order was not accepted',
    'emma.order.menuPay': 'Payment: on pick-up or on delivery, to the business.',
    'emma.order.cardBlocked': 'Online card payment: Blocked',
    // Vala B: the ride card and the slide (the same keys as sq.ts / en.ts)
    'emma.ride.title': 'Your ride offer',
    'emma.ride.from': 'From',
    'emma.ride.to': 'To',
    'emma.ride.estimate': 'estimate',
    'emma.ride.total': 'Total',
    'emma.ride.fiscalTaximeter': 'The fiscal receipt is issued by the taximeter',
    'emma.ride.fiscalBusiness': 'The fiscal receipt is issued by the business',
    'emma.ride.validUntil': 'Valid until {time}',
    'emma.ride.lead': 'Here is the offer for your ride:',
    'emma.ride.words': 'Emma’s full message',
    'emma.slide.hint': 'Slide right to confirm, or tap and choose «Confirm».',
    'emma.slide.confirm': 'Confirm',
    'emma.card.booking': 'Booking',
    'emma.card.availability': 'Free times',
    'emma.card.business': 'Businesses',
    'emma.card.order': 'Order',
    'emma.card.approval': 'Waiting for approval',
    'emma.card.approval.note': 'A person with the permission approves it on the case page.',
    'emma.card.progress': 'Progress',
    'emma.card.finance_summary': 'Finance',
    'emma.card.financePeriod': 'Period {period}',
    'emma.card.fin.net_sales': 'Net sales',
    'emma.card.fin.expenses': 'Expenses',
    'emma.card.fin.net_profit': 'Net profit',
    'emma.card.fin.product_margin': 'Product margin',
    'emma.card.unknown': 'unknown',
    'emma.card.task': 'Open tasks',
    'emma.card.due': 'due {date}',
    'emma.card.service': 'Service',
    'emma.card.when': 'When',
    'emma.card.price': 'Price',
    'emma.card.status': 'Status',
    'emma.card.pickSlot': 'Pick {time} at {business}',
    'emma.card.state.needs_information': 'Needs details',
    'emma.card.state.awaiting_confirmation': 'Waiting for your confirmation',
    'emma.card.state.executing': 'In progress…',
    'emma.card.state.completed': 'Done',
    'emma.card.state.failed': 'Did not go through',
    'emma.card.state.cancelled': 'Cancelled',
    'emma.card.state.handed_to_human': 'With the team',
    'emma.card.state.unknown_result': 'Result not known',
    'emma.card.feedback': 'Was this answer useful?',
    'emma.card.feedback.up': 'Useful',
    'emma.card.feedback.down': 'Not useful',
    'emma.card.feedback.thanks': 'Thanks for your rating.',
    'emma.card.feedback.failed': 'The rating was not sent; try again.',
  },
};

const locale: EmmaLocale = document.body.dataset['locale'] === 'sq' ? 'sq' : 'en';

mountEmma({ api: 'site', labels: LABELS[locale], locale: () => locale });
